import { GroupStorage, IConfigurationHolder, ISharePointConfigurationItem, MappingMode, UserMappingOperation } from "../models/auto-generated";

export interface ValidationResult {
  valid: boolean;
  incompatibilities?: string[];
}

/** Validates settings presented to the client for update. */
export class SettingsValidator {
  static validateOnOrganizationUrlChange(settings: IConfigurationHolder): ValidationResult {
    let userPairsCount = 0;
    let permissionPairsCount = 0;
    let specialPermissionsCount = 0;
    /** custom mappings or special permissions are considered invalid once Dynamics 365 Organization URL is changed*/
    settings.sharePoint.sharepointConfig.forEach((item) => {
      userPairsCount += item.userMapping.userPairs.length;
      permissionPairsCount += item.permissionMapping.permissionPairs.length;
      specialPermissionsCount += item.specialPermissions ? item.specialPermissions.length : 0;
    });

    const incompatibilities = [];
    if (userPairsCount > 0) {
      incompatibilities.push(`Custom user mapping(s): ${userPairsCount}`);
    }
    if (permissionPairsCount > 0) {
      incompatibilities.push(`Custom permission mapping(s): ${permissionPairsCount}`);
    }
    if (specialPermissionsCount > 0) {
      incompatibilities.push(`Explicit folder permission(s): ${specialPermissionsCount}`);
    }

    if (incompatibilities.length > 0) {
      return {
        valid: false,
        incompatibilities: incompatibilities
      };
    }
    return { valid: true };
  }

  static validateOnSiteCollectionUrlChange(settings: IConfigurationHolder, key: number): ValidationResult {
    const item = settings.sharePoint.sharepointConfig.find((element) => element.key === key);
    return this.validate(settings, item);
  }

  static validateOnGroupStorageChange(settings: IConfigurationHolder): ValidationResult {
    const validationResult = this.validate(settings, settings.sharePoint.sharepointConfig[0]);
    if (settings.includeMsTeamsDocumentLocations && settings.groupStorage === GroupStorage.SharePoint) {
      const incompatibility = `Enable Microsoft Teams Support: On`;
      validationResult.valid = false;
      validationResult.incompatibilities = validationResult.incompatibilities ? [...validationResult.incompatibilities, incompatibility] : [incompatibility];
    }
    return validationResult;
  }

  /**
   * Validates settings.
   * @param settings Settings for update.
   * @param item SharePoint configuration item modified or affected by changes.
   */
  private static validate(settings: IConfigurationHolder, item: ISharePointConfigurationItem): ValidationResult {
    const incompatibilities = [];
    if (item.userMapping.mode === MappingMode.REPLACE && settings.groupStorage !== GroupStorage.SharePoint) {
      incompatibilities.push(`User mapping mode: MANUAL`);
    }
    const userPairs = item.userMapping.userPairs.filter((u) => u.operation !== UserMappingOperation.Block);
    if (userPairs.length > 0) {
      incompatibilities.push(`Custom user mapping: ${userPairs.length}`);
    }
    const permissionPairs = item.permissionMapping.permissionPairs;
    if (permissionPairs.length > 0) {
      incompatibilities.push(`Custom permission mapping: ${permissionPairs.length}`);
    }
    const specialPermissions = item.specialPermissions;
    if (specialPermissions && specialPermissions.length > 0) {
      incompatibilities.push(`Explicit folder permission(s): ${specialPermissions.length}`);
    }

    if (incompatibilities.length > 0) {
      return {
        valid: false,
        incompatibilities: incompatibilities
      };
    }
    return { valid: true };
  }
}
